import {
  computed,
  onScopeDispose,
  ref,
  toValue,
  watch,
  type ComputedRef,
  type MaybeRefOrGetter,
} from 'vue'

import { insightAuthoringApi } from '../api/InsightAuthoringApi'
import type {
  AuthoringSessionSummary,
  InsightAuthoringHistoryEntry,
} from '../models/insightAuthoring.types'
import type { WorkbenchAsyncState } from '../models/workbench.types'
import { groupAuthoringHistory } from '../service/insightAuthoringHistory'

import { useInsightsRbac } from './useInsightsRbac'

import { ApiError, type Paginated } from '@/shared/http'

interface InsightAuthoringHistoryPort {
  history: (
    params?: { limit?: number; offset?: number },
    signal?: AbortSignal,
  ) => Promise<Paginated<InsightAuthoringHistoryEntry>>
}

export interface UseInsightAuthoringHistoryOptions {
  canAuthor?: MaybeRefOrGetter<boolean>
  api?: InsightAuthoringHistoryPort
}

export interface UseInsightAuthoringHistory {
  sessions: ComputedRef<readonly AuthoringSessionSummary[]>
  state: ComputedRef<WorkbenchAsyncState>
  error: ComputedRef<ApiError | null>
  load: () => Promise<void>
}

export function useInsightAuthoringHistory(
  options: UseInsightAuthoringHistoryOptions = {},
): UseInsightAuthoringHistory {
  const rbac = options.canAuthor === undefined ? useInsightsRbac() : null
  const canAuthor = computed(() =>
    options.canAuthor === undefined
      ? (rbac?.canAuthorInsights.value ?? false)
      : toValue(options.canAuthor),
  )
  const api = options.api ?? insightAuthoringApi
  const sessions = ref<AuthoringSessionSummary[]>([])
  const state = ref<WorkbenchAsyncState>('idle')
  const error = ref<ApiError | null>(null)
  let generation = 0
  let controller: AbortController | null = null

  function reset(): void {
    generation += 1
    controller?.abort()
    controller = null
    sessions.value = []
    state.value = 'idle'
    error.value = null
  }

  async function load(): Promise<void> {
    if (!canAuthor.value) {
      reset()
      return
    }
    const request = ++generation
    controller?.abort()
    const active = new AbortController()
    controller = active
    state.value = 'loading'
    error.value = null
    try {
      const response = await api.history({ limit: 100, offset: 0 }, active.signal)
      if (request !== generation || active.signal.aborted) return
      sessions.value = groupAuthoringHistory(response.results, 10)
      state.value = sessions.value.length > 0 ? 'ready' : 'empty'
    } catch (cause) {
      if (request !== generation || active.signal.aborted) return
      const normalized = ApiError.isApiError(cause) ? cause : ApiError.fromAxios(cause)
      if (normalized.kind === 'canceled') return
      error.value = normalized
      state.value = 'error'
    }
  }

  watch(canAuthor, () => void load(), { immediate: true })
  onScopeDispose(reset)

  return {
    sessions: computed(() => sessions.value),
    state: computed(() => state.value),
    error: computed(() => error.value),
    load,
  }
}
